import { X } from 'lucide-react';
import React, { useState, useEffect } from 'react';

import { Button } from '@/components/ui/Button';
import { POPUP_BANNER } from '@/config/site';

const STORAGE_KEY = 'popup_banner_last_shown';

export const PopupBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Ha nincs engedélyezve, ne jelenjen meg
    if (!POPUP_BANNER.enabled) {
      return;
    }

    // Ellenőrizzük, hogy ma már megjelent-e
    const lastShown = localStorage.getItem(STORAGE_KEY);
    const today = new Date().toDateString();

    if (lastShown !== today) {
      // Ha ma még nem jelent meg, várunk egy kicsit és megjelenítjük
      const timer = setTimeout(() => {
        setIsVisible(true);
        localStorage.setItem(STORAGE_KEY, today);
      }, 2000); // 2 másodperc késleltetés az oldal betöltése után

      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleButtonClick = () => {
    window.open(POPUP_BANNER.buttonUrl, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {/* Backdrop - áttetsző fekete overlay */}
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9998] animate-fade-in"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Popup Container */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
        <div className="relative bg-black border-2 border-neutral-800 max-w-md w-full pointer-events-auto animate-slide-up shadow-glow-lg">
          {/* Corner Accents */}
          <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-neon-red" />
          <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-neon-red" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-neon-red" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-neon-red" />

          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-neutral-400 hover:text-neon-red transition-colors z-10 group"
            aria-label="Bezárás"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Content */}
          <div className="p-8 pt-12">
            {/* Title with Glitch Effect */}
            <div className="relative mb-6">
              <h2 className="text-3xl md:text-4xl font-black uppercase leading-tight md:leading-tight tracking-tighter text-neon-red text-center relative z-10">
                {POPUP_BANNER.title}
              </h2>
              {/* Glitch shadow effect */}
              <div className="absolute inset-0 text-3xl md:text-4xl font-black uppercase leading-tight md:leading-tight tracking-tighter text-neon-red opacity-20 blur-sm animate-pulse">
                {POPUP_BANNER.title}
              </div>
            </div>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="h-[2px] bg-gradient-to-r from-transparent via-neutral-700 to-transparent" />
              <div className="absolute inset-0 h-[2px] bg-gradient-to-r from-transparent via-neon-red to-transparent opacity-50 blur-sm" />
            </div>

            {/* Message */}
            <p className="text-neutral-300 text-center mb-8 leading-relaxed font-mono text-sm">
              {POPUP_BANNER.message}
            </p>

            {/* Buttons */}
            <div className="flex flex-col gap-3">
              {/* CTA Button */}
              <Button size="lg" onClick={handleButtonClick}>
                {POPUP_BANNER.buttonText}
              </Button>

              {/* Close Button (Text) */}
              <Button variant="ghost" size="sm" onClick={handleClose}>
                {POPUP_BANNER.closeButtonText}
              </Button>
            </div>
          </div>

          {/* Industrial pattern overlay */}
          <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,rgba(255,255,255,0.02)_10px,rgba(255,255,255,0.02)_20px)] pointer-events-none" />
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }
      `}</style>
    </>
  );
};
